import { toLocalDateTimeString } from '../utils/localDateTime';
/**
 * Intermediate product order splitting: from the aggregated demand of an intermediate product
 * (delivered by the backend preview endpoint) plus its master data and current stock, concrete
 * production orders are computed (count, lot size, dates).
 *
 * Strategy (mirrors the old backend buildSuggestedOrders, now in the frontend for code
 * consistency with campaignSplittingService.ts):
 *   - shortage  = target quantity - initialStock - lots already planned
 *   - splitCount = max(1, ceil(shortage / maxLotSize))   (or 1 when maxLotSize is null)
 *   - lotSize   = ceil(shortage / splitCount), rounded up to lotSizeMultiple,
 *                 at least minLotSize, at most maxLotSize
 *   - plannedEnd[k]   = nextDemand - leadTimeDays + k * spreadDays   (the deadline)
 *   - plannedStart[k] = plannedEnd[k] - routingDurationDays          (the desired start)
 *   - earliestStart[k] = null   (default; the user can set it per lot)
 *
 * The coverage (minCoverageDays) is folded into the target value:
 *   target quantity = max(computed demand, avgDailyDemand * minCoverageDays)
 * The frontend can compute this default and put it into the target field as a starting value.
 */

export interface IntermediateDemandAggregate {
  intermediateProductUuid: string;
  name: string;
  baseUnit: string | null;
  /** Pure BOM aggregation: what one would produce without the stock and coverage correction. */
  totalScheduleDemand: number;
  /** Demand peaks from the campaigns: first demand quantity and time (for the date calculation). */
  nextDemandAmount: number | null;
  nextDemandDate: string | null;
  /** Current stock (may be null -> treated as 0). */
  currentStock: number | null;
  /** Number of days the stock lasts - may be null, then it is not relevant. */
  coverageDays: number | null;
  /** Constants from the intermediate product master data. */
  minLotSize: number | null;
  maxLotSize: number | null;
  lotSizeMultiple: number | null;
  minCoverageDays: number | null;
  leadTimeDays: number | null;
  /** Estimated routing duration in days (ceil(seconds/86400)); comes from the backend. */
  routingDurationDays?: number | null;
  /** End hour of the production day (0-23, default 6) from the SystemSettings; it determines a lot's
   *  default earliestStart: production day start = plannedEnd day - 1 at endHour. */
  productionDayEndHour?: number | null;
}

export interface IntermediateLotPlan {
  /** Stable local id for the React key and for editing. */
  rowId: string;
  lotSize: number;
  plannedStart: string | null;
  plannedEnd: string | null;
  /** Hard lower bound for the scheduler, overridable by the user; default null = no constraint. */
  earliestStart: string | null;
}

export interface IntermediateSplitResult {
  /** Target quantity computed by the splitter (demand plus coverage buffer); the default for the UI. */
  defaultSollMenge: number;
  /** Lots computed from target quantity - stock - already planned. */
  lots: IntermediateLotPlan[];
}

export interface SplitInput {
  aggregate: IntermediateDemandAggregate;
  /** Target quantity set by the user; null = use the default (compute defaultSollMenge). */
  sollMenge?: number | null;
  /** Quantity already covered by other sources (e.g. existing orders). */
  alreadyPlannedSum?: number;
}

/**
 * Standard splitting call - returns both the default target quantity (for initializing the UI)
 * and the resulting lots.
 */
export function splitIntermediateOrders(input: SplitInput): IntermediateSplitResult {
  const a = input.aggregate;
  const sollMenge = input.sollMenge != null ? input.sollMenge : computeDefaultSollMenge(a);
  const stock = a.currentStock ?? 0;
  const alreadyPlanned = input.alreadyPlannedSum ?? 0;
  const shortage = Math.max(0, sollMenge - stock - alreadyPlanned);

  if (shortage <= 0) {
    return { defaultSollMenge: sollMenge, lots: [] };
  }

  const maxLot = a.maxLotSize ?? null;
  const splitCount = (maxLot != null && maxLot > 0)
    ? Math.max(1, Math.ceil(shortage / maxLot))
    : 1;

  let perOrder = shortage / splitCount;
  perOrder = roundUpToMultiple(perOrder, a.lotSizeMultiple);
  if (a.minLotSize != null && perOrder < a.minLotSize) perOrder = a.minLotSize;
  if (maxLot != null && perOrder > maxLot) perOrder = maxLot;

  const nextDemand = a.nextDemandDate ? new Date(a.nextDemandDate) : new Date();
  const leadTimeDays = a.leadTimeDays ?? 0;
  const routingDays = a.routingDurationDays ?? 0;
  const minCoverage = Math.max(1, a.minCoverageDays ?? 1);
  const spreadDays = Math.max(1, Math.floor(minCoverage / Math.max(1, splitCount)));

  // The backend treats plannedEnd as a SOFT dueDate (not latestEnd) - see
  // IntermediateOrderConverter. So we align the day with the end of the production day
  // (productionDayEndHour, default 06:00 local): plannedEnd day = nextDemand day - leadTimeDays
  // at endHour. With a tight lead time the solver simply runs late.
  const endHour = a.productionDayEndHour ?? null;
  const basePlannedEnd = endHour != null
    ? atEndHour(addDays(nextDemand, -leadTimeDays), endHour)
    : addDays(nextDemand, -leadTimeDays);
  const baseStart = addDays(basePlannedEnd, -routingDays);

  const lots: IntermediateLotPlan[] = [];
  for (let k = 0; k < splitCount; k++) {
    const offset = k * spreadDays;
    const lotPlannedEnd = addDays(basePlannedEnd, offset);
    lots.push({
      rowId: localId(),
      lotSize: perOrder,
      // Local wall clock for the backend's LocalDateTime fields (review round 5, finding 1.4)
      plannedStart: toLocalDateTimeString(addDays(baseStart, offset)),
      plannedEnd: toLocalDateTimeString(lotPlannedEnd),
      earliestStart: endHour != null ? toLocalDateTimeString(productionDayStart(lotPlannedEnd, endHour)) : null,
    });
  }
  return { defaultSollMenge: sollMenge, lots };
}

/** Sets the time of day of a date to endHour:00 local, without moving the day. */
function atEndHour(date: Date, endHour: number): Date {
  const d = new Date(date);
  d.setHours(endHour, 0, 0, 0);
  return d;
}

/**
 * Production day start for a lot: on the day before the plannedEnd date, at endHour (local time).
 * Mirrors DateUtils.productionDayStart() in the backend.
 */
function productionDayStart(plannedEnd: Date, endHour: number): Date {
  const start = new Date(plannedEnd);
  start.setDate(start.getDate() - 1);
  start.setHours(endHour, 0, 0, 0);
  return start;
}

/**
 * Target quantity without a user override: max(BOM demand, avgDaily * minCoverageDays).
 */
export function computeDefaultSollMenge(a: IntermediateDemandAggregate): number {
  const baseDemand = a.totalScheduleDemand ?? 0;
  const minCoverage = a.minCoverageDays ?? 1;
  // avgDaily is not in the aggregate - approximate it from demand / coverage, otherwise use the demand
  const avgDaily = a.coverageDays != null && a.coverageDays > 0
    ? baseDemand / a.coverageDays
    : baseDemand;
  const coverageTarget = avgDaily * Math.max(1, minCoverage);
  return Math.max(baseDemand, coverageTarget);
}

function roundUpToMultiple(value: number, multiple: number | null | undefined): number {
  if (multiple == null || multiple <= 0) return value;
  return Math.ceil(value / multiple) * multiple;
}

/**
 * Splits a concrete forecast quantity into individual lots according to maxLotSize/minLotSize/lotSizeMultiple
 * - without any stock/demand logic. Used in the wizard save path: a user forecast (for example 120 kg) with
 * maxLotSize=60 produces 2 lots of 60 kg.
 *
 * Returns the list of lot quantities. For a null or <= 0 quantity -> an empty list.
 */
export function splitForecastQuantity(
  quantity: number,
  master: { maxLotSize?: number | null; minLotSize?: number | null; lotSizeMultiple?: number | null }
): number[] {
  if (!Number.isFinite(quantity) || quantity <= 0) return [];
  const maxLot = master.maxLotSize ?? null;
  const splitCount = (maxLot != null && maxLot > 0)
    ? Math.max(1, Math.ceil(quantity / maxLot))
    : 1;
  let perLot = quantity / splitCount;
  perLot = roundUpToMultiple(perLot, master.lotSizeMultiple);
  if (master.minLotSize != null && perLot < master.minLotSize) perLot = master.minLotSize;
  if (maxLot != null && perLot > maxLot) perLot = maxLot;
  return Array.from({ length: splitCount }, () => perLot);
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date.getTime());
  next.setDate(next.getDate() + days);
  return next;
}

function localId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `lot-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}
